import { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";

import Header from "./Header";
import Footer from "./Footer/Footer";
import BannerInformation from "./BannerInformation";
import PageBackground from "./PageBackground";
import SkipLink, { Main } from "./SkipLink";

import { CSS_VARIABLES } from "enums/cssVariables";

import { useHasBackgroundImage } from "hooks/useHasBackgroundImage";

import { CustomizationBackground } from "types/customizationType";

type Props = {
    children: ReactElement;
    isSocketError?: boolean;
    customizationBackground?: CustomizationBackground;
};

const Layout = ({
    children,
    isSocketError,
    // backgroundUrl is the default customization background; only the waiting and vote pages have their own.
    customizationBackground = CustomizationBackground.LOGIN,
}: Props) => {
    const [isOffline, setIsOffline] = useState(false);
    const hasBackgroundImage = useHasBackgroundImage(customizationBackground);

    useEffect(() => {
        function handleOffline() {
            setIsOffline(true);
        }
        function handleOnline() {
            setIsOffline(false);
        }

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);
        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        };
    }, []);

    let bannerMessage;

    if (isOffline) {
        bannerMessage = "offlineMessage";
    } else if (isSocketError) {
        bannerMessage = "socketConnectError";
    }

    const banner = bannerMessage ? (
        <BannerInformation message={bannerMessage} />
    ) : null;

    return (
        <>
            <SkipLink />
            {/* Always in the page, so the banner is announced when it appears (RGAA 7.5) */}
            <div role="status">{banner}</div>
            <Wrapper data-has-background-image={hasBackgroundImage}>
                <PageBackground
                    customizationBackground={customizationBackground}
                />
                <Content>
                    <Header />
                    <Main>{children}</Main>
                    <Footer />
                </Content>
            </Wrapper>
        </>
    );
};

const Wrapper = styled.div`
    /* Positioning reference for the background layer. */
    position: relative;
    max-width: 620px;
    margin: auto;
    min-height: calc(var(--vh, 1vh) * 100);
    display: flex;
    flex-direction: column;
    @media (max-width: 768px) {
        max-width: 350px;
    }
    /* Over a background image, unselected proposals show the frame background; otherwise each keeps its own. */
    &[data-has-background-image="true"] {
        ${CSS_VARIABLES.UNSELECTED_OPTION_BACKGROUND_COLOR}: transparent;
    }
`;

const Content = styled.div`
    z-index: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

export default Layout;
